import { createSignal, For, onMount, Show } from "solid-js";
import { BRAND } from "./brand.ts";
import { Backdrop } from "./Backdrop.tsx";
import { count } from "./count.ts";
import { shareUrl } from "./gameStore.ts";
import { Intro, seenIntro } from "./Intro.tsx";
import { Me } from "./Me.tsx";
import { Nav } from "./Nav.tsx";
import { recentGames } from "./recent.ts";
import { artUrl, readMine, readShelf, renameGame, type ShelfItem } from "./shelf.ts";
import { sunoKind } from "./suno.ts";
import { isTrackLink } from "./track.ts";
import { youtubeId } from "./youtube.ts";

/**
 * The front door.
 *
 * Everything else here is reached by a link somebody was sent. This is the page for arriving
 * with nothing, and until now that meant landing in a visualiser's control room and having to
 * work out that a game was in there somewhere.
 *
 * One box. A song link makes a game and a profile link makes a shelf of them, which is the
 * whole product said in one sentence and one field.
 */
export function Home() {
  const [link, setLink] = createSignal("");
  const [made, setMade] = createSignal<readonly ShelfItem[]>([]);
  const [owned, setOwned] = createSignal<readonly ShelfItem[]>([]);
  const [telling, setTelling] = createSignal(!seenIntro());
  const mine = recentGames();

  /** Which of your own games is having its name changed, if any. */
  const [renaming, setRenaming] = createSignal("");
  const [draft, setDraft] = createSignal("");
  const [naming, setNaming] = createSignal(false);

  const startRename = (item: ShelfItem): void => {
    setRenaming(item.id);
    setDraft(item.title);
  };

  const keepName = async (id: string): Promise<void> => {
    const wanted = draft().trim();
    if (wanted === "") return;
    setNaming(true);
    const kept = await renameGame(id, wanted);
    setNaming(false);
    // A failure leaves the box open with the words still in it, rather than eating them.
    if (kept === null) return;
    setOwned(owned().map((item) => (item.id === id ? { ...item, title: kept } : item)));
    setRenaming("");
  };

  // Arriving with nothing should still show something somebody made, rather than a field and a void.
  onMount(() => {
    void readShelf("game").then((page) => setMade(page.items.slice(0, 6)));
    // Signed in, yours follow you; otherwise this browser's memory is all there is.
    void readMine().then((page) => setOwned(page.items.slice(0, 4)));
  });

  const go = (): void => {
    const value = link().trim();
    if (!isTrackLink(value)) return;
    count("paste");
    location.href = `${location.origin}${location.pathname}?suno=${encodeURIComponent(value)}`;
  };

  const what = (): string => {
    const kind = sunoKind(link().trim());
    if (kind === "profile") return "Open the whole catalogue";
    if (kind !== null) return "Make the game";
    return "Make a game";
  };

  /** Said back as you type, so a link that will work looks different from one that will not. */
  const reads = (): string => {
    const value = link().trim();
    if (value === "") return "";
    const kind = sunoKind(value);
    if (kind === "profile") return "A Suno profile. Every song on it.";
    if (kind !== null) return "A Suno song. Read straight through, in seconds.";
    if (youtubeId(value) !== null) return "A video. Listened to once, on a computer.";
    if (isTrackLink(value)) return "An audio file. Read straight through, in seconds.";
    return "Not a link this can use yet.";
  };

  return (
    <div class="app app--bare home">
      <Backdrop />
      <Nav />
      <Me />
      <Show when={telling()}>
        <Intro onDone={() => setTelling(false)} />
      </Show>
      <header class="hero">
        <p class="hero__over">{BRAND}</p>
        <h1 class="hero__title">
          Any song becomes
          <em> something to play</em>
        </h1>
        <p class="hero__what">
          Paste a link. It listens once, writes the notes, and hands you a game you can send to
          anybody.
        </p>
      </header>

      <div class="home__paste">
        <input
          class="text"
          type="url"
          inputmode="url"
          autocomplete="off"
          placeholder="Paste a Suno, YouTube or audio link"
          value={link()}
          onInput={(event) => setLink(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") go();
          }}
        />
        <button disabled={!isTrackLink(link())} onClick={go}>
          {what()}
        </button>
      </div>

      <Show
        when={reads() !== ""}
        fallback={
          <ul class="takes">
            <li>Suno songs and profiles</li>
            <li>YouTube videos</li>
            <li>Any audio file</li>
          </ul>
        }
      >
        <p class="reads" classList={{ "reads--no": !isTrackLink(link()) }}>
          {reads()}
        </p>
      </Show>

      <p class="home__hint">
        A song or a file is read straight through, which takes seconds and works on a phone. A video
        has to be listened to, once, on a computer, and is then a game for everybody.
      </p>

      {/*
        Two things, said as two.

        The studio was a grey line at the bottom of a page about games, which is a strange way to
        treat half of what this is. Somebody who came to make pictures should not have to read
        past a paste box to find out they were welcome.
      */}
      <div class="doors">
        <a class="door door--play" href="?made">
          <span class="door__kind">Play</span>
          <span class="door__title">Games people made</span>
          <span class="door__what">Songs turned into something you can score.</span>
        </a>
        <a class="door door--make" href="?studio">
          <span class="door__kind">Create</span>
          <span class="door__title">The visual studio</span>
          <span class="door__what">Scenes, looks and colour, for their own sake.</span>
        </a>
      </div>

      <Show when={owned().length > 0}>
        <h2 class="home__heading">Yours, wherever you sign in</h2>
        <ul class="shelf__list home__made-list">
          <For each={owned()}>
            {(item) => (
              <li class="card">
                <a class="card__go" href={shareUrl(item.id)}>
                  <span class="card__art">
                    <Show
                      when={(item.art ?? "") !== ""}
                      fallback={<i class="card__none" aria-hidden="true" />}
                    >
                      <img src={artUrl(item.art)} alt="" loading="lazy" />
                    </Show>
                    <i class="card__play" aria-hidden="true" />
                  </span>
                  <span class="card__words">
                    <span class="card__title">{item.title === "" ? "Untitled" : item.title}</span>
                    <span class="card__under">Yours</span>
                  </span>
                </a>
                <Show
                  when={renaming() === item.id}
                  fallback={
                    <button
                      class="card__rename"
                      aria-label={`Rename ${item.title === "" ? "Untitled" : item.title}`}
                      onClick={() => startRename(item)}
                    >
                      Rename
                    </button>
                  }
                >
                  <form
                    class="card__naming"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void keepName(item.id);
                    }}
                  >
                    <input
                      value={draft()}
                      maxLength={80}
                      autofocus
                      aria-label="New name"
                      onInput={(event) => setDraft(event.currentTarget.value)}
                    />
                    <button type="submit" disabled={naming() || draft().trim() === ""}>
                      Save
                    </button>
                    <button type="button" onClick={() => setRenaming("")}>
                      Cancel
                    </button>
                  </form>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={mine.length > 0}>
        <h2 class="home__heading">Yours</h2>
        <ul class="shelf__list home__made-list">
          <For each={mine.slice(0, 4)}>
            {(entry) => (
              <li class="card">
                <a class="card__go" href={shareUrl(entry.id)}>
                  <span class="card__art">
                    <i class="card__none" aria-hidden="true" />
                    <i class="card__play" aria-hidden="true" />
                  </span>
                  <span class="card__words">
                    <span class="card__title">{entry.title === "" ? "Untitled" : entry.title}</span>
                    <span class="card__under">Yours</span>
                  </span>
                </a>
              </li>
            )}
          </For>
        </ul>
        <a class="home__fine" href="?sets">
          Open your shelf, and make a set
        </a>
      </Show>

      {/* Always here, because somebody with nothing of their own has the most to gain from it. */}
      <Show when={made().length > 0}>
        <h2 class="home__heading">Made by people</h2>
        <ul class="shelf__list home__made-list">
          <For each={made()}>
            {(item) => (
              <li class="card">
                <a class="card__go" href={shareUrl(item.id)}>
                  <span class="card__art">
                    <Show
                      when={(item.art ?? "") !== ""}
                      fallback={<i class="card__none" aria-hidden="true" />}
                    >
                      <img src={artUrl(item.art)} alt="" loading="lazy" />
                    </Show>
                    <i class="card__play" aria-hidden="true" />
                  </span>
                  <span class="card__words">
                    <span class="card__title">{item.title === "" ? "Untitled" : item.title}</span>
                    <Show when={(item.who ?? "") !== ""}>
                      <span class="card__under">by {item.who}</span>
                    </Show>
                  </span>
                </a>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <a class="home__made" href="?made">
        See everything people have made
      </a>
      <a class="home__fine" href="?queue">
        Make a run of songs
      </a>
      <a class="home__fine" href="?scoring">
        How scoring works
      </a>
      <a class="home__fine" href="?privacy">
        What this keeps
      </a>
    </div>
  );
}
